import styled from '@emotion/styled';
import { useRef, useState } from 'react';
import { useDrop } from 'react-dnd';

import { useIsRTL } from '../../../hooks/useIsRTL';

import { su } from '../../../theme/functions/su';
import { motion } from '../../../theme/motion';
import { theme } from '../../../theme/theme';

import { dragTypes } from '../../../components/dnd/dragTypes';

export function ZoneNavigationSortLine({ dropZone, dragId, descendants, onDrop }) {
  const [selectedParentIndex, setSelectedParentIndex] = useState(0);
  const domRef = useRef(null);
  const isRTL = useIsRTL();

  const validParentDrop = !dropZone.parents.find((parent) => {
    // Can't drop into a child of the dragged item
    return dragId === parent.id;
  });

  const validParentsBefore = dropZone.parentsBefore.filter((parent) => {
    // 1. check if the parent is a child of the dragged item, if so we want to prevent dropping into it
    // 2. we want to allow dropping into the parent levels that are not children of the dragged item
    return dragId !== parent.id && !descendants[dragId]?.has(parent.id);
  });

  let validParents = [];

  if (dropZone.parentsBefore.length > dropZone.parents.length) {
    const diff = dropZone.parentsBefore.length - dropZone.parents.length + 1; // for the equal part
    validParents = validParentsBefore.slice(0, diff);
  }

  if (validParents.length === 0) {
    validParents = dropZone.parents.slice(0, 1).filter((parent) => {
      return !descendants[dragId]?.has(parent.id);
    });
  }

  // Reverse the array so that the first item is the closest parent
  validParents.reverse();

  const selectedParent = validParents[selectedParentIndex] || validParents[0];

  const [{ isOver, canDrop }, dropRef] = useDrop({
    accept: [dragTypes.ZONE],
    collect: (dropMonitor) => {
      return {
        isOver: dropMonitor.isOver({ shallow: true }),
        canDrop: dropMonitor.canDrop(),
      };
    },
    canDrop(dragItem, dropMonitor) {
      return dragItem.id === dragId && validParents.length > 0 && validParentDrop;
    },
    hover(dragItem, monitor) {
      if (!monitor.isOver({ shallow: true })) return;

      const clientOffset = monitor.getClientOffset();
      const sourceClientOffset = monitor.getSourceClientOffset();

      if (!clientOffset || !domRef.current) return;

      const hoverBoundingRect = domRef.current.getBoundingClientRect();
      const dragItemMargin = (dragItem.level || 0) * 20;

      let hoverClientX;
      if (isRTL) {
        hoverClientX = hoverBoundingRect.right - clientOffset.x + dragItemMargin;
      } else {
        hoverClientX = sourceClientOffset.x - hoverBoundingRect.left + dragItemMargin;
      }

      // Divide horizontal space into zones for each possible parent
      const extraVisualSpace = 5;
      const hoveredLevel = Math.floor((hoverClientX - extraVisualSpace) / 20);
      const hoveredIndex = hoveredLevel - validParents[0].level;
      const clampedIndex = Math.max(0, Math.min(hoveredIndex, validParents.length - 1));

      if (clampedIndex !== selectedParentIndex) {
        setSelectedParentIndex(clampedIndex);
      }
    },
    drop(dragItem, monitor) {
      if (selectedParent) {
        const isParent = dropZone.parents.some((parent) => parent.id === selectedParent.id);
        const dropId = isParent ? dropZone.beforeItemId : null;

        onDrop({
          type: dragTypes.ZONE,
          dropId,
          dragId: dragItem.id,
          position: isParent ? 'above' : 'below',
          targetParent: selectedParent.id,
        });
      }
    },
  });

  if (validParents.length === 0) return null;

  return (
    <S.DropZone
      ref={dropRef(domRef)}
      data-is-over={isOver}
      data-can-drop={canDrop}
      data-level-depth={validParents.length}
    >
      {canDrop && (
        <S.SortLine
          data-is-over={isOver}
          style={{
            '--level-margin': `${selectedParent?.level * 20 + 10}px`,
          }}
        />
      )}
    </S.DropZone>
  );
}

const S = {};

S.DropZone = styled.div`
  position: relative;
  height: ${su(2)};
  margin-block: ${su(-1)};
  margin-inline: ${su(1)};
  cursor: pointer;
  z-index: 300;
  pointer-events: auto;
`;

S.SortLine = styled.div`
  position: absolute;
  inset-inline: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 3px;
  background-color: ${theme.color.highlight};
  border-radius: 2px;
  margin-inline-start: var(--level-margin);
  margin-inline-end: ${su(1)};
  pointer-events: none;
  opacity: 0;
  transition: opacity ${motion.duration.micro} ${motion.curve.fast_in};

  &[data-is-over='true'] {
    opacity: 1;
  }

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    inset-inline-start: -2.5px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    border: 3px solid ${theme.color.highlight};
    background-color: ${theme.color.component};
  }
`;
